import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import StartupVisibilityModal from './modals/StartupVisibilityModal';
import { EyeOff as EyeOffIcon } from 'lucide-react';

const HelpPanel = ({ candidateStream }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showStartupVisibilityModal, setShowStartupVisibilityModal] = useState(false);
  const navigate = useNavigate();

  const streamLower = (candidateStream || "").toLowerCase();
  const isSWE = streamLower.includes("swe") || streamLower.includes("software engineering");
  const isSales = streamLower.includes("sales");
  const isMarketingCandidate = streamLower.includes("marketing");
  const isGeneralistCandidate = streamLower.includes("generalist");

  const getInterviewGuidanceLink = () => {
    if (isSales) {
      return "/resources";
    }
    if (isMarketingCandidate) {
      return "/resources";
    }
    // Default to general resources hub for SWE, generalist, and other streams
    return "/resources";
  };

  // Helper functions for interview coaching
  const getCalendlyLink = (sessionType) => {
    if (isSales) {
      switch (sessionType) {
        case 'first':
          return 'https://calendly.com/d/cv4g-cnm-zrd/jumpstart-sales-first-interview-prep-call';
        case 'second':
          return 'https://calendly.com/d/cr5q-bx7-235/jumpstart-sales-role-play-practise-call';
        case 'final':
          return 'https://calendly.com/kabir-7/catch-up-regarding-jumpstart?month=2025-07';
        default:
          return 'https://calendly.com/d/cv4g-cnm-zrd/jumpstart-sales-first-interview-prep-call';
      }
    } else {
      // Generalist and Marketing
      switch (sessionType) {
        case 'first':
          return 'https://calendly.com/d/cv8m-cj2-9qz/jumpstart-generalists-first-interview-prep-call?month=2025-07';
        case 'second':
          return 'https://calendly.com/naim-jumpstart-uk/interview-coaching-generalist-marketing?month=2025-07';
        case 'final':
          return 'https://calendly.com/kabir-7/catch-up-regarding-jumpstart?month=2025-07';
        default:
          return 'https://calendly.com/d/cv8m-cj2-9qz/jumpstart-generalists-first-interview-prep-call?month=2025-07';
      }
    }
  };

  const getSessionDescription = (sessionType) => {
    if (isSales) {
      switch (sessionType) {
        case 'first':
          return {
            title: "First Interview Prep (Mandatory)",
            description: "Your elevator pitch, essential prep tips, and Q&A time",
            coach: "Gemma or Channon",
            points: [
              "Your elevator pitch - live run-through with feedback",
              "Essential prep tips - how to frame your story",
              "Q&A time - ask anything about the process"
            ]
          };
        case 'second':
          return {
            title: "Role Play Practice (Optional)",
            description: "Prepare for roleplays and case studies",
            coach: "Gemma or Channon",
            points: [
              "Cold call/roleplay task practice",
              "Feedback & second run-through",
              "Q&A about the process"
            ]
          };
        case 'final':
          return {
            title: "Final Round Support (Optional)",
            description: "Polish your performance for the final round",
            coach: "Kabir",
            points: [
              "Advanced interview techniques",
              "Final round preparation",
              "Performance optimization"
            ]
          };
      }
    } else {
      // Generalist and Marketing
      switch (sessionType) {
        case 'first':
          return {
            title: "First Interview Prep (Mandatory)",
            description: "Your elevator pitch, essential prep tips, and Q&A time",
            coach: "Edward",
            points: [
              "Your elevator pitch - live run-through with feedback",
              "Essential prep tips - how to frame your story",
              "Q&A time - ask anything about the process"
            ]
          };
        case 'second':
          return {
            title: "Second Round Support (Optional)",
            description: "Extra guidance before your second round",
            coach: "Naim",
            points: [
              "Refine your interview approach",
              "Practice advanced scenarios",
              "Get personalised feedback"
            ]
          };
        case 'final':
          return {
            title: "Final Round Support (Optional)",
            description: "Polish your performance for the final round",
            coach: "Kabir",
            points: [
              "Advanced interview techniques",
              "Final round preparation",
              "Performance optimization"
            ]
          };
      }
    }
  };

  const handleStartupVisibilityComplete = () => {
    setShowStartupVisibilityModal(false);
  };

  const handleResourcesClick = () => {
    navigate(getInterviewGuidanceLink());
    setIsOpen(false);
  };

  const faqs = [
    {
      question: "How many roles can I apply for?",
      answer: "We recommend applying for around 7 roles per week to maximise your chances of success. Aim for quality over quantity - choose roles you're genuinely interested in and where your experience aligns. While there's no hard cap, we don't expect anyone to apply for more than 15 roles per week. Avoid the 'spray and pray' approach - targeted, thoughtful applications are far more effective."
    },
    {
      question: "When can I expect to hear back from my applications?",
      answer: "This should be maximum a week, but hopefully a lot sooner!"
    },
    {
      question: "What does a typical startup interview process look like?",
      answer: (
        <>
          Initial call → case study → final interview. Check out{' '}
          <button 
            onClick={() => {
              navigate('/resources');
              setIsOpen(false);
            }}
            className="text-[#02B491] hover:underline font-medium"
          >
            this page
          </button>
          {' '}for details!
        </>
      )
    },
    {
      question: "I've been introduced to a startup. What does this mean?",
      answer: "If we email you introducing you to a startup, this means they want to interview you - make sure you follow up with your availability ASAP!"
    },
    {
      question: "I'm still waiting to hear back from applications. What does this mean?",
      answer: "If you haven't heard back from an application, it's not necessarily bad news. It just means that the startup is yet to review your application (we're chasing them, don't worry!)"
    },
    {
      question: "Will I hear back if my application is not successful?",
      answer: "Yes - regardless as to whether your application is accepted or declined, you will receive an update from Jumpstart team. If you haven't heard back, this means your application is still pending & we're working hard to get an update for you. Ps. don't forget to check your junk!"
    },
    {
      question: "Will I always get an interview at the startups I pick?",
      answer: "We see that about ~70%+ of requests to interview are accepted, although there are always some anomalies."
    },
    {
      question: "Should I wait for the start-up to respond after Jumpstart's introduction?",
      answer: "If you receive an email introducing to a startup this means that they have explicitly said YES to your application. Be proactive and make the first move (don't forget to share interview availability!)"
    },
    {
      question: "What happens if I don't get any interviews?",
      answer: "Don't worry! You can make applications every day of the week, so we'd recommend making more applications pronto. If you need support or want to make any changes to your profile reach out to the team."
    }
  ];

  return (
    <>
      {/* Help Button - Top Right */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 right-4 z-30 w-8 h-8 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-full transition-all duration-200 flex items-center justify-center group shadow-sm"
        aria-label="Help & Support"
      >
        <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
          ?
        </span>
      </button>

      {/* Help Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />
            
                         {/* Panel */}
             <motion.div
               initial={{ x: "100%" }}
               animate={{ x: 0 }}
               exit={{ x: "100%" }}
               transition={{ type: "spring", damping: 25, stiffness: 300 }}
               className="fixed right-0 top-0 h-full w-full max-w-lg bg-white z-50 overflow-hidden border-l border-gray-100"
             >
                             {/* Header */}
               <div className="relative p-6 border-b border-gray-100">
                 <button
                   onClick={() => setIsOpen(false)}
                   className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                 >
                   <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
                 
                 <div>
                   <h2 className="text-lg font-semibold text-gray-900">Help</h2>
                 </div>
               </div>

                             {/* Content */}
               <div className="overflow-y-auto h-full pb-24">
                 <div className="p-6 space-y-8">
                   
                   {/* Platform Guide Video */}
                   <div className="space-y-4">
                     <div className="border border-gray-200 rounded-lg overflow-hidden">
                       <div className="p-4 border-b border-gray-100">
                         <div className="flex items-center gap-3">
                           <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                             <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                           </div>
                           <div>
                             <h3 className="font-medium text-gray-900">Platform Guide</h3>
                             <p className="text-sm text-gray-500">Watch the video walkthrough</p>
                           </div>
                         </div>
                       </div>
                       <div className="relative w-full h-48 bg-gray-50">
                         <iframe
                           src="https://www.loom.com/embed/c7c9a233982b4d43a3139ad3d3f01f9c?sid=71eb1307-04aa-4afd-88a6-5587976be2db"
                           frameBorder="0"
                           allowFullScreen
                           className="absolute inset-0 w-full h-full"
                           title="Platform Guide Video"
                         />
                       </div>
                     </div>
                   </div>

                   {/* Interview Coaching */}
                   <div className="space-y-4">
                     <div className="flex items-center gap-3">
                       <div className="w-8 h-8 bg-[#02B491] bg-opacity-10 rounded-lg flex items-center justify-center">
                         <svg className="w-4 h-4 text-[#02B491]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                         </svg>
                       </div>
                       <div>
                         <h3 className="font-medium text-gray-900">Interview Coaching</h3>
                         <p className="text-sm text-gray-500">Book sessions with our expert coaches</p>
                       </div>
                     </div>

                     {/* First Interview Prep */}
                     <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                       <div className="flex items-center justify-between">
                         <div>
                           <h4 className="font-medium text-gray-900 text-sm">{getSessionDescription('first').title}</h4>
                           <p className="text-xs text-gray-600 mt-1">{getSessionDescription('first').description}</p>
                         </div>
                         <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Required</span>
                       </div>
                       <div className="text-xs text-gray-600">
                         <span className="font-medium">Coach:</span> {getSessionDescription('first').coach}
                       </div>
                       <a
                         href={getCalendlyLink('first')}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-block w-full text-center px-3 py-2 text-xs font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-md transition-colors"
                       >
                         Book First Interview Prep
                       </a>
                     </div>

                     {/* Second Round Support */}
                     <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                       <div>
                         <h4 className="font-medium text-gray-900 text-sm">{getSessionDescription('second').title}</h4>
                         <p className="text-xs text-gray-600 mt-1">{getSessionDescription('second').description}</p>
                       </div>
                       <div className="text-xs text-gray-600">
                         <span className="font-medium">Coach:</span> {getSessionDescription('second').coach}
                       </div>
                       <a
                         href={getCalendlyLink('second')}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-block w-full text-center px-3 py-2 text-xs font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-md transition-colors"
                       >
                         Book Second Round Prep
                       </a>
                     </div>

                     {/* Final Round Support */}
                     <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                       <div>
                         <h4 className="font-medium text-gray-900 text-sm">{getSessionDescription('final').title}</h4>
                         <p className="text-xs text-gray-600 mt-1">{getSessionDescription('final').description}</p>
                       </div>
                       <div className="text-xs text-gray-600">
                         <span className="font-medium">Coach:</span> {getSessionDescription('final').coach}
                       </div>
                       <a
                         href={getCalendlyLink('final')}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="inline-block w-full text-center px-3 py-2 text-xs font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-md transition-colors"
                       >
                         Book Final Round Prep
                       </a>
                     </div>
                   </div>

                   {/* Quick Actions */}
                   <div className="space-y-3">
                     <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                       <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                         <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                         </svg>
                       </div>
                       <div className="flex-1">
                         <h3 className="font-medium text-gray-900">Contact Support</h3>
                         <a href="mailto:info@jumpstart-uk.com" className="text-sm text-gray-600 hover:text-gray-900">
                           info@jumpstart-uk.com
                         </a>
                       </div>
                     </div>

                     <button
                       onClick={handleResourcesClick}
                       className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left w-full"
                     >
                       <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                         <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                         </svg>
                       </div>
                       <div className="flex-1">
                         <h3 className="font-medium text-gray-900">Interview Resources</h3>
                         <p className="text-sm text-gray-500">Stream-specific guidance</p>
                       </div>
                       <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                       </svg>
                     </button>

                     <button
                       onClick={() => setShowStartupVisibilityModal(true)}
                       className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left w-full"
                     >
                       <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                         <EyeOffIcon className="w-4 h-4 text-gray-600" />
                       </div>
                       <div className="flex-1">
                         <h3 className="font-medium text-gray-900">Manage Visibility</h3>
                         <p className="text-sm text-gray-500">Hide your profile from specific companies</p>
                       </div>
                     </button>
                   </div>

                                     {/* FAQs */}
                   <div>
                     <div className="mb-4">
                       <h3 className="font-medium text-gray-900">Frequently Asked Questions</h3>
                     </div>
                     
                     <div className="space-y-1">
                       {faqs.map((faq, index) => (
                         <details key={index} className="group border border-gray-200 rounded-lg">
                           <summary className="flex items-center justify-between py-4 px-4 cursor-pointer hover:bg-gray-50 transition-colors">
                             <h4 className="font-medium text-gray-900 text-sm leading-relaxed">
                               {faq.question}
                             </h4>
                             <svg className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                             </svg>
                           </summary>
                           <div className="px-4 pb-4 border-t border-gray-100">
                             <p className="text-sm text-gray-600 leading-relaxed pt-3">
                               {faq.answer}
                             </p>
                           </div>
                         </details>
                       ))}
                     </div>
                   </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Startup Visibility Modal */}
      {showStartupVisibilityModal && (
        <StartupVisibilityModal
          isOpen={showStartupVisibilityModal}
          onClose={() => setShowStartupVisibilityModal(false)}
          onComplete={handleStartupVisibilityComplete}
        />
      )}
    </>
  );
};

export default HelpPanel; 